import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
  selectScreenName,
  selectSubscriptionList,
} from 'root-redux/selects/common'

import { compareSubscriptionList } from 'helpers/compareSubscriptionList'

import { setSelectedSubscriptionAction } from 'modules/purchase/redux/actions/common'

import { eventLogger } from 'services/eventLogger.service'

import { BUNDLE_IDENTIFIER } from 'root-constants'

import { SINGLE_SUBSCRIPTION_AS_NUMBER } from '../components/SubscriptionsUpsellBlock/constants'

export const useUpsellSubscriptions = (isAutoBundle: boolean) => {
  const dispatch = useDispatch()
  const subscriptions = useSelector(
    selectSubscriptionList,
    compareSubscriptionList,
  )
  const screenName = useSelector(selectScreenName)

  const bundleSubscription = useMemo(
    () =>
      subscriptions.find(({ product }) => product.includes(BUNDLE_IDENTIFIER)),
    [subscriptions],
  )

  const singleSubscriptions = useMemo(
    () =>
      subscriptions.filter(
        ({ product }) => !product.includes(BUNDLE_IDENTIFIER),
      ),
    [subscriptions],
  )

  const [selectedProducts, setSelectedProducts] = useState<string[]>([])

  useEffect(() => {
    if (!isAutoBundle || !singleSubscriptions.length) return

    setSelectedProducts([singleSubscriptions[0].product])
  }, [isAutoBundle, singleSubscriptions])

  useEffect(() => {
    if (!selectedProducts.length || !singleSubscriptions.length) return

    if (
      selectedProducts.length > SINGLE_SUBSCRIPTION_AS_NUMBER &&
      bundleSubscription
    ) {
      dispatch(setSelectedSubscriptionAction(bundleSubscription))
      return
    }

    const matchingSubscription = singleSubscriptions.find(
      ({ product }) => product === selectedProducts[0],
    )

    if (matchingSubscription) {
      dispatch(setSelectedSubscriptionAction(matchingSubscription))
    }
  }, [selectedProducts, singleSubscriptions, bundleSubscription, dispatch])

  const handleChange = useCallback(
    (product: string) => {
      if (selectedProducts.includes(product)) {
        eventLogger.logUpsellSubscriptionDisable({
          screenName,
          upsellName: product,
        })
        setSelectedProducts(
          selectedProducts.filter(
            (selectedProduct) => selectedProduct !== product,
          ),
        )
        return
      }

      eventLogger.logUpsellSubscriptionEnable({
        screenName,
        upsellName: product,
      })
      setSelectedProducts([...selectedProducts, product])
    },
    [screenName, selectedProducts],
  )

  const selectAll = useCallback(() => {
    setSelectedProducts(singleSubscriptions.map(({ product }) => product))

    if (bundleSubscription) {
      dispatch(setSelectedSubscriptionAction(bundleSubscription))
    }
  }, [singleSubscriptions, bundleSubscription, dispatch])

  return {
    singleSubscriptions,
    bundleSubscription,
    selectedProducts,
    handleChange,
    selectAll,
    hasSelectedProducts: selectedProducts.length > 0,
  }
}
